import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button, TextField, Typography, Box } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import LoaderIndicator from "../../Utility/loading";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Cell,
  LabelList,
  PieChart,
  Pie,
} from "recharts";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { getAllFilesList, handleLogout } from "../../Utility/utility";
import { BASE_URL, appVersion } from "../../App_Settings/app_settings";
import {
  getUserIdFromLocalStorage,
  getUserFileAuditByUserId,
} from "../../API_Endpoints/APIEndpoints";
import "./dashboard.css";
import { screenHeaderStyle, useSharedValue } from "../Constants/constants";
import { useHistory } from "react-router-dom";

const DashboardCard = ({ title, children, onDownload }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = (format) => {
    onDownload(format);
    handleMenuClose();
  };

  return (
    <div className="dashboard-card">
      {/* <div className="card-header">
        <Typography variant="h7" className="card-title">{title}</Typography> */}
      {/* <div className="card-actions">
          <Tooltip title="Download Options">
            <IconButton onClick={handleMenuClick} size="small">
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            elevation={3}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => handleDownload('excel')}>
              <FileDownloadIcon fontSize="small" /> Export to Excel
            </MenuItem>
            <MenuItem onClick={() => handleDownload('pdf')}>
              <PictureAsPdfIcon fontSize="small" /> Export to PDF
            </MenuItem>
          </Menu>
        </div> */}
      {/* </div> */}
      <Typography variant="h7" className="card-title">
        {title}
      </Typography>
      <div className="card-content">{children}</div>
    </div>
  );
};

// Custom tooltip component for charts
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p>{`${payload[0].payload.name}: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

// Custom label for bar charts
const renderCustomBarLabel = (props) => {
  const { x, y, width, height, value } = props;
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={12}
      fontWeight="bold"
    >
      {value}
    </text>
  );
};

export default function DashboardPage() {
  const history = useHistory();
  // State management
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [allFiles, setAllFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [statics, setStatics] = useState([]);
  const { defaultBusinessunit, defaultOrganization, defaultUser } = useSharedValue();

  // User role state
  const [userRole, setUserRole] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Helper function to determine if user is admin or super admin
  const isAdminUser = () => {
    return userRole === "Admin";
  };

  // Helper function to get appropriate report endpoint based on user role
  const getReportEndpoint = () => {
    if (defaultUser && defaultUser.userId) {
      return `${BASE_URL}/FileImportReport/UserReport/${defaultUser.userId}`;
    } else if (defaultOrganization && defaultOrganization.organizationId) {
      return `${BASE_URL}/FileImportReport/OrganizationReport/${defaultOrganization.organizationId}`;
    } else if (isAdminUser()) {
      // Admin or Super Admin - use organization report
      return `${BASE_URL}/FileImportReport/OrganizationReport/${getUserIdFromLocalStorage().organizationId}`;
    } else {
      // Normal user - use user report
      return `${BASE_URL}/FileImportReport/UserReport/${getUserIdFromLocalStorage().userId}`;
    }
  };

  // Dashboard activity timestamps
  const [lastLogin, setLastLogin] = useState(null);
  const [lastExtracted, setLastExtracted] = useState(null);
  const [lastImported, setLastImported] = useState(null);
  const [lastExported, setLastExported] = useState(null);

  // Chart data for all three real-time charts
  const [fileProcessingFunnelData, setFileProcessingFunnelData] = useState([]);
  const [workflowStatusData, setWorkflowStatusData] = useState([]);
  const [fileLifecycleData, setFileLifecycleData] = useState([]);

  // Forms statistics
  const [formsData, setFormsData] = useState({});

  // Statistics
  const [totalImported, setTotalImported] = useState(0);
  const [userImported, setUserImported] = useState(0);
  const [totalProcessed, setTotalProcessed] = useState(0);
  const [userProcessed, setUserProcessed] = useState(0);
  const [userNotProcessed, setUserNotProcessed] = useState(0);
  const [userPendingApproval, setUserPendingApproval] = useState(0);
  const [waitingForReview, setWaitingForReview] = useState(0);
  const [totalApproved, setTotalApproved] = useState(0);
  const [totalRejected, setTotalRejected] = useState(0);
  const { isMenuOpen } = useSharedValue();

  // Forms Statistics
  const [totalForms, setTotalForms] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [idDocuments, setIdDocuments] = useState(0);
  const [withholdingStatements, setWithholdingStatements] = useState(0);
  const [otherDocuments, setOtherDocuments] = useState(0);

  // Menu handling

  // Format stored dates to display format
  const formatStoredDate = (dateString) => {
    const date = dateString ? new Date(dateString) : new Date();
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Memoized date filter function to prevent unnecessary re-renders
  const applyDateFilter = useCallback(() => {
    let filtered = [...allFiles];

    if (startDate && endDate) {
      const startDateTime = new Date(startDate).setHours(0, 0, 0, 0);
      const endDateTime = new Date(endDate).setHours(23, 59, 59, 999);

      filtered = allFiles.filter((file) => {
        const uploadedDate = new Date(file.uploadedDate).getTime();
        return uploadedDate >= startDateTime && uploadedDate <= endDateTime;
      });
    }

    setFilteredFiles(filtered);
    updateStatistics(filtered);
    updateChartData(filtered);
  }, [startDate, endDate, allFiles]);

  // Date filter handler - only runs when dates or files change
  useEffect(() => {
    if (allFiles.length > 0) {
      applyDateFilter();
    }
  }, [applyDateFilter, allFiles]);

  // Use ref to track previous filter values to detect actual changes
  const prevFiltersRef = useRef({
    businessunit: defaultBusinessunit,
    organization: defaultOrganization,
    user: defaultUser,
  });

  // Add render counter to track re-renders
  const renderCountRef = useRef(0);
  const fetchCountRef = useRef(0);

  // Log on every render to understand what's changing
  useEffect(() => {
    renderCountRef.current += 1;
  });

  useEffect(() => {
    // Abort controller to handle React 18 Strict Mode double mounting
    let isMounted = true;

    const fetchData = async () => {
      if (!isMounted) {
        return;
      }

      fetchCountRef.current += 1;

      try {
        setLoading(true);

        // Helper function to handle API responses and check for 401 errors
        const handleApiResponse = async (response) => {
          if (response.status === 401) {
            handleLogout(history);
            return null;
          }
          return await response.json();
        };

        const userId = getUserIdFromLocalStorage().userId;
        const organizationId = getUserIdFromLocalStorage().organizationId;
        const token = getUserIdFromLocalStorage().token;

        const [filesData, userRoleResponse, auditData] = await Promise.all([
          (async () => {
            const result = await getAllFilesList(
              history,
              defaultOrganization,
              defaultBusinessunit,
              defaultUser
            );

            return result;
          })(),

          (async () => {
            const result = await fetch(`${BASE_URL}/users/${userId}/${organizationId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            return result;
          })(),

          (async () => {
            const result = await getUserFileAuditByUserId();

            return result;
          })(),
        ]);

        const userRoleData = await handleApiResponse(userRoleResponse);

        // Determine if user is admin based on parallel-fetched role data
        let isAdminUser = false;
        if (userRoleData) {
          const userRoles = userRoleData.userRoles || [];
          isAdminUser = userRoles.some((role) => role.name === "Admin");
        }

        const getReportEndpoint = () => {
          if (defaultUser && defaultUser.id) {
            return `${BASE_URL}/FileImportReport/UserReport/${defaultUser.id}`;
          } else if (defaultOrganization && defaultOrganization.id) {
            return `${BASE_URL}/FileImportReport/OrganizationReport/${defaultOrganization.id}`;
          } else if (isAdminUser) {
            return `${BASE_URL}/FileImportReport/OrganizationReport/${organizationId}`;
          } else {
            return `${BASE_URL}/FileImportReport/UserReport/${userId}`;
          }
        };

        const reportResponse = await fetch(getReportEndpoint(), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*",
          },
        });

        let reportResult = { success: false, data: null };
        if (reportResponse.ok) {
          const reportData = await reportResponse.json();
          reportResult = { success: true, data: reportData };
        } else if (reportResponse.status === 401) {
          handleLogout(history);
          return;
        }

        if (!filesData) {
          console.log("❌ No files data - stopping execution");
          return; // Stop execution if 401 occurred
        }

        setAllFiles(filesData);

        // Use the same filesData for overview chart - it's already filtered for the user
        overAllChartData(filesData);

        // Initial filter to set the filtered files
        let initialFiltered = filesData;
        setFilteredFiles(initialFiltered);
        updateStatistics(initialFiltered);
        updateChartData(initialFiltered);

        // Process report data
        if (reportResult && reportResult.success && reportResult.data) {
          processFormsData(reportResult.data);
        } else if (
          reportResult &&
          reportResult.success &&
          (!reportResult.data || reportResult.data.length === 0)
        ) {
          processFormsData([]);
        } else {
          // Actual error case
          processFormsData([]);
        }

        if (Array.isArray(auditData) && auditData.length > 0) {
          // Find last imported (latest 'Uploaded')
          const lastImportedItem = auditData
            .filter((item) => item.action === "Uploaded" && item.actionDate)
            .sort((a, b) => new Date(b.actionDate) - new Date(a.actionDate))[0];
          setLastImported(lastImportedItem ? formatStoredDate(lastImportedItem.actionDate) : null);

          // Find last extracted (latest 'Extracted')
          const lastExtractedItem = auditData
            .filter((item) => item.action === "Extracted" && item.actionDate)
            .sort((a, b) => new Date(b.actionDate) - new Date(a.actionDate))[0];
          setLastExtracted(
            lastExtractedItem ? formatStoredDate(lastExtractedItem.actionDate) : null
          );

          // Find last exported (latest 'Exported')
          const lastExportedItem = auditData
            .filter((item) => item.action === "Exported" && item.actionDate)
            .sort((a, b) => new Date(b.actionDate) - new Date(a.actionDate))[0];
          setLastExported(lastExportedItem ? formatStoredDate(lastExportedItem.actionDate) : null);
        } else {
          setLastImported(null);
          setLastExtracted(null);
          setLastExported(null);
        }
      } catch (error) {
        console.error("❌ Error fetching dashboard data:", error);
        // Check if error is related to authorization
        if (error.message && error.message.includes("401")) {
          handleLogout(history);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Update previous filter values after fetch
    prevFiltersRef.current = {
      businessunit: defaultBusinessunit,
      organization: defaultOrganization,
      user: defaultUser,
    };

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [defaultBusinessunit, defaultOrganization, defaultUser, history]);

  // Process forms data from the report
  const processFormsData = (reportData) => {
    if (!reportData || !reportData.length) {
      return;
    }

    // Aggregate form data across all reports - using direct properties instead of JSON parsing
    const aggregatedData = reportData.reduce((acc, item, itemIndex) => {
      try {
        if (!item) {
          console.warn("⚠️ Skipping invalid report item:", item);
          return acc;
        }

        // Initialize totals if not already done
        if (!acc.totalPages) {
          acc.totalPages = 0;
        }
        if (!acc.totalForms) {
          acc.totalForms = 0;
        }
        if (!acc["ID Docs"]) {
          acc["ID Docs"] = 0;
        }
        if (!acc["WHS"]) {
          acc["WHS"] = 0;
        }
        if (!acc["Other"]) {
          acc["Other"] = 0;
        }

        // Add total pages from direct property
        const totalPages = item.totalPage;

        if (totalPages !== undefined && totalPages !== null) {
          const pagesToAdd = parseInt(totalPages) || 0;
          acc.totalPages += pagesToAdd;
        }

        // Add total forms from direct property
        const totalForms = item.totalForms;

        if (totalForms !== undefined && totalForms !== null) {
          const formsToAdd = parseInt(totalForms) || 0;
          acc.totalForms += formsToAdd;
        }

        // Process tax forms based on taxForms field
        const taxForms = item.taxForms;

        if (taxForms) {
          // Map tax form types to categories
          if (taxForms.includes("Withholding Statement")) {
            acc["WHS"] += 1;
          } else if (
            taxForms.includes("W8") ||
            taxForms.includes("W9") ||
            taxForms.includes("Self-Cert")
          ) {
            // These are tax forms but not withholding statements
            // We'll count them under forms but not specifically categorize
          } else {
            // Other document types
            acc["Other"] += 1;
          }
        }
      } catch (error) {
        console.error(`❌ Error processing report item ${itemIndex + 1}:`, error);
      }
      return acc;
    }, {});

    setFormsData(aggregatedData);

    // Create chart data with the three main categories, plus total pages and total forms
    const chartData = [
      { name: "Pages", value: aggregatedData.totalPages || 0, color: "#8B5CF6" },
      { name: "Forms", value: aggregatedData.totalForms || 0, color: "#EC4899" },
      { name: "ID Documents", value: aggregatedData["ID Docs"] || 0, color: "#3B82F6" },
      { name: "Withholding Statements", value: aggregatedData["WHS"] || 0, color: "#10B981" },
      { name: "Other", value: aggregatedData["Other"] || 0, color: "#F59E0B" },
    ];

    setWorkflowStatusData(chartData);
  };

  const updateStatistics = (filesData) => {
    // Update existing statistics
    const userImportedCount = filesData.filter((row) => !row.isProcessed).length;
    const userProcessedCount = filesData.filter(
      (row) => row.isProcessed && !row.reviewedDate
    ).length;
    const userNotProcessedCount = filesData.filter((row) => !row.isProcessed).length;
    const waitingForReviewCount = filesData.filter(
      (row) => row.isProcessed && !row.reviewedDate && !row.status
    ).length;
    const userPendingApprovalCount = filesData.filter(
      (row) => row.reviewedDate && !row.status
    ).length;
    const totalApprovedCount = filesData.filter((row) => row.approvedDate !== null).length;
    const totalRejectedCount = filesData.filter((row) => row.rejectedDate !== null).length;
    const totalProcessedCount = filesData.filter((row) => row.isProcessed).length;
    const totalImportedCount = filesData.length;

    setUserImported(userImportedCount);
    setUserProcessed(userProcessedCount);
    setUserNotProcessed(userNotProcessedCount);
    setWaitingForReview(waitingForReviewCount);
    setUserPendingApproval(userPendingApprovalCount);
    setTotalApproved(totalApprovedCount);
    setTotalRejected(totalRejectedCount);
    setTotalProcessed(totalProcessedCount);
    setTotalImported(totalImportedCount);
  };

  const updateChartData = (filesData) => {
    // File Processing Funnel - Shows progression of files through the system
    const uploadedCount = filesData.length;
    const waitingCount = filesData.filter(
      (row) =>
        row.isProcessed === false &&
        row.reviewedDate === null &&
        row.rejectedDate === null &&
        row.approvedDate === null
    ).length;
    const extractedCount = filesData.filter(
      (row) =>
        row.isProcessed === true ||
        row.reviewedDate !== null ||
        row.rejectedDate !== null ||
        row.approvedDate !== null
    ).length;
    const approvedCount = filesData.filter((row) => row.approvedDate !== null).length;
    const rejectedCount = filesData.filter((row) => row.rejectedDate !== null).length;

    const chartData = [
      { name: "Uploaded", value: uploadedCount, color: "#4C96D7" },
      { name: "Waiting", value: waitingCount, color: "#4CBBB0" },
      { name: "Extracted", value: extractedCount, color: "#F3C24A" },
      { name: "Approved", value: approvedCount, color: "#D988BA" },
      { name: "Rejected", value: rejectedCount, color: "#7813d1" },
    ];

    setFileProcessingFunnelData(chartData);
  };

  const overAllChartData = (filesData) => {
    if (!filesData) {
      return;
    }

    // File Lifecycle Overview - Same as Funnel but we might want to display differently
    const uploadedCount = filesData.length;
    const waitingCount = filesData.filter((row) => !row.isProcessed).length;
    const extractedCount = filesData.filter((row) => row.isProcessed).length;
    const approvedCount = filesData.filter(
      (row) => row.isProcessed && row.approvedDate !== null && row.reviewedDate !== null
    ).length;
    const rejectedCount = filesData.filter(
      (row) => row.rejectedDate !== null && row.reviewedDate !== null
    ).length;

    const chartData = [
      { name: "Uploaded", value: uploadedCount, color: "#4C96D7" },
      { name: "Waiting", value: waitingCount, color: "#4CBBB0" },
      { name: "Extracted", value: extractedCount, color: "#F3C24A" },
      { name: "Approved", value: approvedCount, color: "#D988BA" },
      { name: "Rejected", value: rejectedCount, color: "#7813d1" },
    ];

    setFileLifecycleData(chartData);
  };

  // Using centralized getReport function from APIEndpoints.js

  const handleDownload = (chartType, format) => {
    const chartDataMap = {
      "file-processing": fileProcessingFunnelData,
      "workflow-status": workflowStatusData,
      "file-lifecycle": fileLifecycleData,
    };

    const data = chartDataMap[chartType] || [];

    if (format === "pdf") {
      const doc = new jsPDF();
      doc.text(
        chartType
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        14,
        15
      );

      doc.autoTable({
        head: [["Name", "Value"]],
        body: data.map((item) => [item.name, item.value]),
        startY: 25,
      });

      doc.save(`${chartType}-${new Date().toISOString().split("T")[0]}.pdf`);
    } else if (format === "excel") {
      const csvContent =
        "data:text/csv;charset=utf-8," +
        "Name,Value\n" +
        data.map((item) => `${item.name},${item.value}`).join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${chartType}-${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadPDFReport = () => {
    setIsLoading(true);

    getUserFileAuditByUserId()
      .then((data) => {
        // Filter data by date range if dates are selected
        let filteredData = data;
        if (startDate && endDate) {
          filteredData = data.filter((item) => {
            const itemDate = new Date(item.createdDate);
            const startDateTime = new Date(startDate).setHours(0, 0, 0, 0);
            const endDateTime = new Date(endDate).setHours(23, 59, 59, 999);
            return itemDate >= startDateTime && itemDate <= endDateTime;
          });
        }

        // Generate PDF with data
        const doc = generatePDF(filteredData, "Dashboard Report");
        doc.save(`dashboard-report-${new Date().toISOString().split("T")[0]}.pdf`);

        const newDownloadTime = formatStoredDate(new Date());

        localStorage.setItem("downloadTime", new Date().toISOString());

        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error generating PDF report:", error);
        setIsLoading(false);
      });
  };

  const generatePDF = (data, reportName) => {
    const doc = new jsPDF();

    // Add logo
    const logoPath = "/logo.png";
    doc.addImage(logoPath, "PNG", 14, 10, 50, 9);

    // // Add title
    doc.setFontSize(15);
    doc.text(reportName, 14, 25);

    // Add date range if selected
    if (startDate && endDate) {
      const formattedStartDate = new Date(startDate).toLocaleDateString();
      const formattedEndDate = new Date(endDate).toLocaleDateString();
      doc.setFontSize(10);
      doc.text(`Date Range: ${formattedStartDate} to ${formattedEndDate}`, 14, 30);
    }

    // Get user info from localStorage
    const userInfo = getUserIdFromLocalStorage();
    const name = `${userInfo.firstName} ${userInfo.lastName}`;
    const userName = name || "N/A";
    const userEmail = userInfo?.userName || "N/A";
    const organizationName = userInfo?.organizationName || "N/A";

    // Add user info
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 35);
    doc.text(`User: ${userName}`, 14, 40);
    doc.text(`Email: ${userEmail}`, 14, 45);
    doc.text(`Organization: ${organizationName}`, 14, 50);

    doc.setFontSize(12);
    doc.text("Extraction Statistics", 14, 65);

    doc.autoTable({
      head: [["Stage", "Value"]],
      body: fileProcessingFunnelData.map((item) => [item.name, item.value]),
      startY: 70,
      theme: "grid",
      headStyles: { fillColor: [42, 167, 224] },
      styles: { fontSize: 8 },
    });

    doc.setFontSize(12);
    doc.text("Forms Statistics", 14, doc.autoTable.previous.finalY + 15);

    doc.autoTable({
      head: [["Form Type", "Count"]],
      body: workflowStatusData.map((item) => [item.name, item.value]),
      startY: doc.autoTable.previous.finalY + 20,
      theme: "grid",
      headStyles: { fillColor: [42, 167, 224] },
      styles: { fontSize: 8 },
    });

    doc.text("Total Statistics", 14, doc.autoTable.previous.finalY + 15);

    doc.autoTable({
      head: [["Stage", "Value"]],
      body: fileLifecycleData.map((item) => [item.name, item.value]),
      startY: doc.autoTable.previous.finalY + 20,
      theme: "grid",
      headStyles: { fillColor: [42, 167, 224] },
      styles: { fontSize: 8 },
    });

    return doc;
  };

  // NOTE: User audit data fetching has been consolidated into the main useEffect above
  // This eliminates redundant API calls and improves dashboard load performance

  return (
    <div
      style={{
        ...screenHeaderStyle(isMenuOpen),
        overflow: "hidden",
        height: "100vh",
        backgroundColor: "#E8EAEC",
      }}
    >
      <div style={{ padding: "20px", height: "100%", overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 20px",
            borderRadius: "10px",
            backgroundColor: "white",
            marginBottom: "20px",
            height: "80px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "30px" }}>
            {/* Last Imported */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "white",
                padding: "8px 12px",
                borderRadius: "6px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <img
                src="/extract.svg"
                alt="Import"
                style={{ width: "100%", width: "20px", height: "20px", display: "block" }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>
                  Last Imported
                </span>
                <span style={{ fontSize: "13px", color: "#333", fontWeight: 600 }}>
                  {lastImported}
                </span>
              </div>
            </div>
            {/* Last Extracted */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "white",
                padding: "8px 12px",
                borderRadius: "6px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <img
                src="/extract.svg"
                alt="Extracted"
                style={{ width: "100%", width: "20px", height: "20px", display: "block" }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>
                  Last Extracted
                </span>
                <span style={{ fontSize: "13px", color: "#333", fontWeight: 600 }}>
                  {lastExtracted}
                </span>
              </div>
            </div>
            {/* Last Exported */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: "white",
                padding: "8px 12px",
                borderRadius: "6px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <img
                src="/import.svg"
                alt="Exported"
                style={{ width: "100%", width: "20px", height: "20px", display: "block" }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>
                  Last Exported
                </span>
                <span style={{ fontSize: "13px", color: "#333", fontWeight: 600 }}>
                  {lastExported}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    style={{
                      minWidth: "130px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                )}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    style={{
                      minWidth: "130px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                )}
              />
            </LocalizationProvider>

            <Button
              variant="contained"
              onClick={handleDownloadPDFReport}
              disabled={isLoading}
              style={{
                backgroundColor: "#2aa7e0",
                color: "white",
                fontWeight: 600,
                borderRadius: "6px",
                textTransform: "none",
                padding: "8px 16px",
                fontSize: "13px",
                height: "40px",
              }}
            >
              {isLoading ? "Generating..." : "Download Report"}
            </Button>
          </div>
        </div>

        {/* User Account Section */}
        <div style={{ margin: "20px 0", color: "#2aa7e0", fontSize: "18px", fontWeight: 600 }}>
          User: {getUserIdFromLocalStorage().firstName} {getUserIdFromLocalStorage().lastName}
        </div>

        {/* Dashboard Grid - Two Cards Side by Side */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            marginBottom: "24px",
          }}
        >
          <DashboardCard
            title="Total Extractions"
            onDownload={(format) => handleDownload("file-processing", format)}
          >
            {loading ? (
              <LoaderIndicator />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={fileProcessingFunnelData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  barSize={60}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                    interval={0}
                  />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                  <RechartsTooltip content={<CustomTooltip />} cursor={false} />
                  <Bar
                    dataKey="value"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={true}
                    animationDuration={1000}
                  >
                    {fileProcessingFunnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="top"
                      style={{ fontSize: "12px", fontWeight: "bold" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </DashboardCard>

          <DashboardCard
            title="Total Forms"
            onDownload={(format) => handleDownload("workflow-status", format)}
          >
            {loading ? (
              <LoaderIndicator />
            ) : (
              <div style={{ display: "flex", height: 300, alignItems: "center" }}>
                {/* Pie Chart Container */}
                <div style={{ position: "relative", width: "60%", height: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={workflowStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={100}
                        paddingAngle={1}
                        dataKey="value"
                        animationDuration={1000}
                      >
                        {workflowStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Center Total */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <div style={{ fontSize: "16px", fontWeight: "bold", color: "#333" }}>
                      Total:
                    </div>
                    <div style={{ fontSize: "32px", fontWeight: "bold", color: "#2aa7e0" }}>
                      {workflowStatusData.reduce((sum, item) => sum + item.value, 0)}
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div
                  style={{
                    width: "40%",
                    paddingLeft: "20px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: "12px",
                  }}
                >
                  {workflowStatusData.map((entry, index) => (
                    <div
                      key={index}
                      style={{ display: "flex", alignItems: "center", fontSize: "14px" }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: entry.color,
                          marginRight: 12,
                          borderRadius: 3,
                        }}
                      ></div>
                      <div
                        style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                      >
                        <span style={{ color: "#666", fontWeight: 500 }}>{entry.name}</span>
                        <span style={{ fontWeight: "bold", color: "#333" }}>{entry.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DashboardCard>
        </div>
      </div>

      {/* Version label bottom-right */}
      <Box
        sx={{
          position: "fixed",
          bottom: 10,
          right: 14,
          color: "#666666",
          fontSize: "12px",
          opacity: 0.85,
          pointerEvents: "none",
        }}
      >
        v{appVersion}
      </Box>
    </div>
  );
}
